import {
  SALES_INSIGHTS_ACTIVITY_TYPE_KINDS,
  SalesInsightsActivityType,
  SalesInsightsActivityTypeKind
} from '../models/sales-insights.model';

export interface SalesInsightsActivityTypeGroup {
  kind: SalesInsightsActivityTypeKind;
  labelKey: string;
  types: SalesInsightsActivityType[];
}

export const SALES_INSIGHTS_TYPE_ICONS = [
  'fa-phone',
  'fa-envelope',
  'fa-location-dot',
  'fa-users',
  'fa-handshake',
  'fa-person-chalkboard',
  'fa-store',
  'fa-hotel',
  'fa-truck',
  'fa-file-signature',
  'fa-list-check',
  'fa-note-sticky',
  'fa-flag',
  'fa-bullseye',
  'fa-gift',
  'fa-star',
  'fa-mug-hot',
  'fa-calendar-check'
];

// Every kind is listed, also without types, so the admin always sees where a new type ends up.
export function salesInsightsActivityTypeGroups(types: SalesInsightsActivityType[]): SalesInsightsActivityTypeGroup[] {
  return SALES_INSIGHTS_ACTIVITY_TYPE_KINDS.map(kind => ({
    kind,
    labelKey: `SALES_INSIGHTS_ACTIVITY_TYPES_${kind.toUpperCase()}`,
    types: (types || [])
      .filter(type => (type.kind || 'standard') === kind)
      .sort((a, b) => Number(b.active) - Number(a.active) || (a.name || '').localeCompare(b.name || '', 'sv'))
  }));
}

// The settings are the default; a component's own typeColors and typeIcons options win.
export function salesInsightsTypeColors(
  types: SalesInsightsActivityType[],
  overrides?: { [type: string]: number } | null
): { [type: string]: number } {
  const colors: { [type: string]: number } = {};
  (types || []).filter(type => !!type.color).forEach(type => (colors[type.code] = type.color as number));
  return { ...colors, ...(overrides || {}) };
}

export function salesInsightsTypeIcons(
  types: SalesInsightsActivityType[],
  overrides?: { [type: string]: string } | null
): { [type: string]: string } {
  const icons: { [type: string]: string } = {};
  (types || []).filter(type => !!type.icon).forEach(type => (icons[type.code] = type.icon as string));
  return { ...icons, ...(overrides || {}) };
}

export function salesInsightsInactiveTypes(types: SalesInsightsActivityType[]): Set<string> {
  return new Set((types || []).filter(type => !type.active).map(type => type.code));
}

// A visit is booked on the visit type when exactly one active type counts as a visit.
export function salesInsightsVisitTypeCode(types: SalesInsightsActivityType[]): string | null {
  const visits = (types || []).filter(type => type.active && type.countsAsVisit);
  return visits.length === 1 ? visits[0].code : null;
}

// The goal's own type when it has one, otherwise its only type; null leaves the create modal on its own default.
export function salesInsightsGoalTypeCode(
  types: SalesInsightsActivityType[],
  goalId: string | null | undefined,
  goalTypes?: string[] | null
): string | null {
  if (!goalId) {
    return null;
  }
  const own = (types || []).find(type => type.kind === 'goal' && type.goalId === goalId);
  if (own) {
    return own.code;
  }
  return goalTypes?.length === 1 ? goalTypes[0] : null;
}
